import { type CSSProperties } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { Link } from "react-router-dom";
import igorPhoto from "@/assets/igor-nikitin-card.jpg";

/* ═══════════════════════════════════════════════════════════════
   «AI-трансформация на практике» — блок процесса (шаги 01–05).
   Подача «Маршрут»: восходящая тропа уровней рядом с заголовком,
   гора с флагом на вершине, карточки-лесенка 01–05, CTA снизу.
   Текст Cygre центрируется оптически (.optical-center).

   Интерактив: тропа в покое пустая (серая); hover на карточку N
   заполняет её оранжевым ровно до узла «Уровень N», узлы зажигаются
   по пути, карточка мягко отзывается (подъём, тень, растущая
   чёрточка, номер в accent). На карточке 05 линия доходит до
   последнего узла и флажок на вершине подскакивает. Убрал курсор —
   заполнение мягко стекает обратно в ноль.
   ═══════════════════════════════════════════════════════════════ */

type Step = {
  num: string;
  title: string;
  desc: string;
};

const steps: Step[] = [
  {
    num: "01",
    title: "Обучаем руководство и команду",
    desc: "Выстраиваем общее понимание, цели и правила трансформации и готовим команду к погружению в ИИ",
  },
  {
    num: "02",
    title: "Собираем и выбираем инициативы",
    desc: "Определяем то, что действительно важно для бизнеса и подлежит улучшению с помощью ИИ",
  },
  {
    num: "03",
    title: "Проверяем реализуемость, данные и экономику",
    desc: "Отсекаем то, что не окупится, с минимальными затратами.",
  },
  {
    num: "04",
    title: "Внедряем решения и запускаем пилоты",
    desc: "Интегрируем пилоты ИИ-инструментов и выводим рабочие решения в процессы",
  },
  {
    num: "05",
    title: "Масштабируем то, что показало эффект",
    desc: "Приумножаем успешные кейсы, усиливаем результат в деньгах и открываем новые возможности",
  },
];

const easeOut = [0.22, 1, 0.36, 1] as const;

/* ─── Тропа уровней (полоса 330px, viewBox 0 0 1000 330) ───
   Геометрия снята с референса: узлы спускаются в зону лесенки карточек,
   гора с флагом — справа над финальным подъёмом линии. */
const levels = [
  { x: 1.7, y: 327 },
  { x: 24, y: 299 },
  { x: 45.4, y: 276 },
  { x: 67.5, y: 245 },
  { x: 87.4, y: 203 },
];

// Смещение карточек вниз (лесенка): 01 — ниже всех, 05 — выше всех.
const cardOffsets = [96, 70, 56, 38, 0];

// Подъёмы — прямые диагонали с небольшими скруглениями углов
const TRAIL_D =
  "M -20,327 L 122,327 Q 134,327 141,320 L 163,306 Q 170,299 182,299 L 322,299 Q 334,299 341,293 L 361,282 Q 368,276 380,276 L 562,276 Q 574,276 581,269 L 603,252 Q 610,245 622,245 L 758,245 Q 770,245 777,237 L 803,211 Q 810,203 822,203 L 932,203 Q 946,203 954,197 L 1060,148";

/* Прогресс тропы считаем ПО ГОРИЗОНТАЛИ (доля ширины viewBox 0..1):
   узлы стоят на lv.x% ширины, так что совпадение фронта заполнения
   с узлами точное на любой ширине экрана. */
const nodeT = levels.map((lv) => lv.x / 100);

// Цель заполнения при hover на карточку N — ровно узел «Уровень N»:
// линия останавливается в точке, не проскакивая дальше.
const hoverTargets = nodeT;

/* Оранжевая «пройденная» часть тропы: копия пути поверх серого,
   обрезанная прямоугольным clip-ом, растущим слева направо.
   Никаких stroke-dasharray: дэши с pathLength врут в Chrome при
   non-scaling-stroke + preserveAspectRatio="none" (недолёт до узлов
   на широких экранах). Clip-прямоугольник в юзер-координатах viewBox
   растягивается вместе с svg — фронт всегда на v·ширины. */
const TrailFill = ({ progress }: { progress: MotionValue<number> }) => {
  /* Рект начинается на x=-20 (старт пути), фронт = ровно v·1000 —
     та же шкала долей ширины, в которой стоят узлы (lv.x/100) */
  const clipWidth = useTransform(progress, (v) => 20 + v * 1000);
  return (
    <g>
      <defs>
        <clipPath id="wmt-trail-fill-clip">
          <motion.rect x={-20} y={0} height={330} style={{ width: clipWidth }} />
        </clipPath>
      </defs>
      <path
        d={TRAIL_D}
        fill="none"
        stroke="hsl(var(--accent))"
        strokeWidth="2"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        clipPath="url(#wmt-trail-fill-clip)"
      />
    </g>
  );
};

/* Узел уровня «зажигается», когда прогресс линии до него дотянулся */
const NodeFill = ({
  progress,
  t,
  x,
  y,
}: {
  progress: MotionValue<number>;
  t: number;
  x: number;
  y: number;
}) => {
  /* Узел загорается на подходе линии и полностью зажжён к моменту,
     когда фронт доходит до его центра (цель hover = ровно узел) */
  const opacity = useTransform(progress, [t - 0.04, t - 0.01], [0, 1]);
  const scale = useTransform(progress, [t - 0.04, t - 0.01], [0.5, 1]);
  return (
    <motion.span
      className="absolute h-4 w-4 rounded-full border-2 border-accent bg-accent"
      style={{ left: `${x}%`, top: `${y}px`, x: "-50%", y: "-50%", opacity, scale }}
    />
  );
};

const TrailPath = ({ progress }: { progress: MotionValue<number> | null }) => (
  <div className="relative hidden h-[330px] lg:block">
    {/* Соединительная линия (растягивается по ширине, штрих не масштабируется) */}
    <svg
      viewBox="0 0 1000 330"
      preserveAspectRatio="none"
      className="absolute inset-0 h-full w-full"
      aria-hidden="true"
    >
      <path
        d={TRAIL_D}
        fill="none"
        stroke="hsl(var(--border))"
        strokeWidth="2"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      {progress && <TrailFill progress={progress} />}
    </svg>

    {/* Узлы + подписи уровней */}
    {levels.map((lv, i) => (
      <div key={i}>
        <span
          className="absolute -translate-x-1/2 -translate-y-full whitespace-nowrap text-[13px] font-medium tracking-normal text-muted-foreground/80"
          style={{ left: `${lv.x}%`, top: `${lv.y - 22}px` }}
        >
          Уровень {i + 1}
        </span>
        <span
          className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-muted-foreground/40 bg-background"
          style={{ left: `${lv.x}%`, top: `${lv.y}px` }}
        />
        {progress && (
          <NodeFill progress={progress} t={nodeT[i]} x={lv.x} y={lv.y} />
        )}
      </div>
    ))}

  </div>
);

const AITransformationPractice = () => {
  /* Цель заполнения тропы: 0 в покое, уровень карточки при hover.
     Пружина сглаживает движение в обе стороны. */
  const fillTarget = useMotionValue(0);
  const progress = useSpring(fillTarget, {
    stiffness: 80,
    damping: 24,
    restDelta: 0.001,
  });

  return (
    <section
      id="transformation-practice"
      className="relative z-10 -mt-8 overflow-hidden rounded-t-[32px] bg-background py-24 sm:-mt-14 sm:rounded-t-[56px] lg:py-32"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Заголовок */}
        <div className="relative z-10 max-w-none">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.75rem] lg:whitespace-nowrap leading-[1.12] tracking-tight text-foreground">
            Системная <span className="text-accent">AI-трансформация</span> бизнеса
          </h2>
          <p className="mt-7 text-[17px] text-muted-foreground leading-relaxed max-w-lg">
            WMT AI помогает среднему и&nbsp;крупному бизнесу пройти путь
            от&nbsp;выбора задач до&nbsp;внедрения решений, обучения команды
            и&nbsp;масштабирования результата
          </p>
        </div>

        {/* Тропа уровней — поднимается вправо рядом с заголовком */}
        <div className="lg:-mt-[185px]">
          <TrailPath progress={progress} />
        </div>

        {/* Карточки-лесенка (смещение — через margin-top, чтобы не конфликтовать
            с inline-transform framer-motion; hover-стили — на внутреннем div) */}
        {/* items-stretch: карточки выше по уровню растягиваются вниз,
            нижняя кромка у всех пяти — одна линия */}
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:-mt-[70px] lg:grid-cols-5">
          {steps.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, ease: easeOut, delay: i * 0.08 }}
              style={{ "--ty": `${cardOffsets[i]}px` } as CSSProperties}
              className="lg:[margin-top:var(--ty)]"
            >
              <div
                onMouseEnter={() => fillTarget.set(hoverTargets[i])}
                onMouseLeave={() => fillTarget.set(0)}
                className="group h-full rounded-2xl border border-border bg-card p-6 transition-all duration-300 ease-out hover:-translate-y-1 hover:border-accent/40 hover:shadow-[0_18px_36px_-24px_hsl(var(--foreground)/0.3)] xl:p-7"
              >
                <div className="optical-center font-heading text-[2.75rem] font-semibold leading-none tabular-nums text-foreground transition-colors duration-300 group-hover:text-accent">
                  {s.num}
                </div>
                <div className="mt-4 h-[2px] w-12 rounded-full bg-accent transition-[width] duration-500 ease-out group-hover:w-full" />
                <h3 className="mt-6 font-heading text-[17px] font-bold leading-[1.3] text-foreground xl:text-[18px]">
                  {s.title}
                </h3>
                <p className="mt-3 text-[14.5px] leading-relaxed text-muted-foreground">
                  {s.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 rounded-2xl border border-border bg-card px-5 py-4 shadow-[0_14px_36px_-30px_hsl(var(--foreground)/0.35)] sm:px-6 lg:mt-20">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-4">
              <img
                src={igorPhoto}
                alt="Игорь Никитин"
                className="size-16 shrink-0 rounded-full object-cover object-[center_28%] sm:size-[72px]"
                loading="lazy"
              />
              <div className="min-w-0">
                <p className="font-heading text-lg font-semibold leading-tight text-foreground">
                  Игорь Никитин
                </p>
                <p className="mt-1.5 max-w-4xl text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
                  AI-трансформация проводится по авторской методологии Игоря Никитина
                </p>
              </div>
            </div>
            <Link
              to="/team/igor_nikitin"
              className="wmt-cta wmt-cta--dark shrink-0 self-start px-6 text-sm sm:self-center"
            >
              <span className="optical-center">Подробнее</span>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AITransformationPractice;
